import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { API_URL } from '../../config';
import {
  VIDEO_CONFIG,
  validateVideo,
  processVideoForUpload,
  formatFileSize,
  formatDuration,
  calculateExpirationDate,
} from '../../utils/video-utils';
import { v4 as uuidv4 } from 'uuid';

/**
 * VideoUploader Component
 * Handles video selection, compression, thumbnail generation, and upload
 * 
 * Props:
 * - videos: Current array of video objects
 * - onVideosChange: Callback when videos array changes
 * - maxVideos: Maximum number of videos allowed (default: 2)
 * - disabled: Whether the uploader is disabled
 * - hideExistingVideos: If true, don't show thumbnails of existing videos (useful when displayed elsewhere)
 */
const VideoUploader = ({ 
  videos = [], 
  onVideosChange, 
  maxVideos = VIDEO_CONFIG.maxVideosPerWorkOrder,
  disabled = false,
  hideExistingVideos = false
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  const canAddMore = videos.length < maxVideos && !disabled;

  /**
   * Upload file to S3 via presigned URL
   */
  const uploadToS3 = async (blob, isThumb = false) => {
    const fileName = isThumb 
      ? `thumbnail_${Date.now()}.jpg` 
      : `video_${Date.now()}.mp4`;
    const fileType = isThumb ? 'image/jpeg' : 'video/mp4';

    // Get presigned URL from backend
    const response = await fetch(`${API_URL}/api/work-orders/get-video-upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ fileName, fileType, isThumb }),
    });

    if (!response.ok) {
      throw new Error('Failed to get upload URL');
    }

    const { uploadUrl, videoUrl, headers } = await response.json();

    // Upload to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: blob,
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload to S3');
    }

    return videoUrl;
  };

  /**
   * Handle video file drop/selection
   */
  const onDrop = useCallback(async (acceptedFiles) => {
    if (!canAddMore) {
      toast.error(`Maximum ${maxVideos} videos allowed`);
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    setIsProcessing(true);
    setProcessingProgress(0);
    setUploadProgress(0);

    try {
      // Validate video
      setProcessingStage('Validating video...');
      const validation = await validateVideo(file);
      
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Process video (compress + generate thumbnail)
      setProcessingStage('Processing video...');
      const result = await processVideoForUpload(file, ({ stage, progress }) => {
        if (stage === 'compressing') {
          setProcessingStage('Compressing video...');
          setProcessingProgress(progress);
        } else if (stage === 'thumbnail') {
          setProcessingStage('Generating thumbnail...');
        }
      });

      // Upload video
      setProcessingStage('Uploading video...');
      setUploadProgress(10);
      const videoUrl = await uploadToS3(result.videoBlob, false);
      setUploadProgress(60);

      // Upload thumbnail
      setProcessingStage('Uploading thumbnail...');
      const thumbnailUrl = await uploadToS3(result.thumbnailBlob, true);
      setUploadProgress(90);

      // Create video object
      const videoObject = {
        id: uuidv4(),
        url: videoUrl,
        thumbnailUrl: thumbnailUrl,
        duration: result.duration,
        sizeBytes: result.compressedSize,
        createdAt: new Date().toISOString(),
        expiresAt: calculateExpirationDate(),
      };

      // Update videos array
      const updatedVideos = [...videos, videoObject];
      onVideosChange(updatedVideos);
      
      setUploadProgress(100);
      
      // Show success message with compression stats
      if (result.wasCompressed && result.compressedSize < result.originalSize) {
        const savedPercent = Math.round((1 - result.compressedSize / result.originalSize) * 100);
        toast.success(
          `Video uploaded! Compressed from ${formatFileSize(result.originalSize)} to ${formatFileSize(result.compressedSize)} (${savedPercent}% smaller)`
        );
      } else {
        toast.success(`Video uploaded successfully! (${formatFileSize(result.compressedSize)})`);
      }

    } catch (error) {
      toast.error(error.message || 'Failed to upload video');
    } finally {
      setIsProcessing(false);
      setProcessingStage('');
      setProcessingProgress(0);
      setUploadProgress(0);
    }
  }, [videos, onVideosChange, canAddMore, maxVideos]);

  /**
   * Remove a video from the list
   */
  const handleRemoveVideo = (videoId) => {
    const updatedVideos = videos.filter(v => v.id !== videoId);
    onVideosChange(updatedVideos);
    toast.success('Video removed');
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': VIDEO_CONFIG.acceptedExtensions,
    },
    maxFiles: 1,
    multiple: false,
    disabled: !canAddMore || isProcessing,
  });

  // Calculate overall progress
  const overallProgress = isProcessing 
    ? processingProgress > 0 
      ? Math.round((processingProgress * 0.6) + (uploadProgress * 0.4))
      : uploadProgress
    : 0;

  return (
    <div className="space-y-4">
      {/* Header with count - only show if not hiding existing videos */}
      {!hideExistingVideos && (
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Videos ({videos.length}/{maxVideos})
          </label>
          {videos.length >= maxVideos && (
            <span className="text-xs text-gray-500">Maximum videos reached</span>
          )}
        </div>
      )}

      {/* Existing Videos - only show if not hiding */}
      {!hideExistingVideos && videos.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {videos.map((video) => (
            <div key={video.id} className="relative group">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={video.thumbnailUrl}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                />
                {/* Play icon overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-12 h-12 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
                {/* Duration badge */}
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                  {formatDuration(video.duration)}
                </div>
              </div>
              {/* Remove button */}
              <button
                type="button"
                onClick={() => handleRemoveVideo(video.id)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                aria-label="Remove video"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drop Zone */}
      {canAddMore && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-blue-500 bg-blue-50'
              : isProcessing
              ? 'border-gray-300 bg-gray-50 cursor-wait'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          
          {isProcessing ? (
            <div className="space-y-3">
              <div className="flex justify-center">
                <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <p className="text-sm text-gray-600">{processingStage}</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500">{overallProgress}%</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-center">
                <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-sm text-gray-600">
                <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
              </div>
              <p className="text-xs text-gray-500">
                MP4, MOV, WebM • Max {VIDEO_CONFIG.maxDuration} seconds • Up to 500MB
              </p>
              <p className="text-xs text-gray-400">
                Videos are automatically compressed for faster upload
              </p>
            </div>
          )}
        </div>
      )}

      {/* Info text - only show if not hiding existing videos */}
      {!hideExistingVideos && (
        <p className="text-xs text-gray-500">
          Videos will be automatically deleted after 60 days
        </p>
      )}
    </div>
  );
};

export default VideoUploader;

